import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { StaffEmploymentService } from "lib-klab-sdks";
import { EmploymentContractsPort } from "../../../app/ports/employment/employment-contracts.port";
import {
  ContractCatalogItem,
  EmployeeDocument,
  EmployeeDocumentRegisterRequest,
  EmploymentContract,
  EmploymentContractStatus,
  EmploymentContractTerminationRequest,
  EmploymentContractUpsertRequest,
} from "../../../domain/models/employment/contract.model";
import {
  mapContractCatalogItem,
  mapEmployeeDocument,
  mapEmployeeDocumentRequest,
  mapEmploymentContract,
  mapEmploymentContractRequest,
} from "../../mappers/employment/contract.mapper";

@Injectable({ providedIn: "root" })
export class EmploymentContractsPortAdapter implements EmploymentContractsPort {
  constructor(private readonly api: StaffEmploymentService) {}

  list(
    employeeId?: number,
    status?: EmploymentContractStatus,
  ): Observable<EmploymentContract[]> {
    return this.api
      .listContracts(employeeId, status)
      .pipe(map((items) => items.map(mapEmploymentContract)));
  }

  getById(contractId: number): Observable<EmploymentContract> {
    return this.api.getContract(contractId).pipe(map(mapEmploymentContract));
  }

  create(
    request: EmploymentContractUpsertRequest,
  ): Observable<EmploymentContract> {
    return this.api
      .createContract(mapEmploymentContractRequest(request))
      .pipe(map(mapEmploymentContract));
  }

  createWithDocument(
    request: EmploymentContractUpsertRequest,
    file: File,
  ): Observable<EmploymentContract> {
    return this.api
      .createContractWithDocument(mapEmploymentContractRequest(request), file)
      .pipe(map(mapEmploymentContract));
  }

  update(
    contractId: number,
    request: EmploymentContractUpsertRequest,
  ): Observable<EmploymentContract> {
    return this.api
      .updateContract(contractId, mapEmploymentContractRequest(request))
      .pipe(map(mapEmploymentContract));
  }

  updateWithDocument(
    contractId: number,
    request: EmploymentContractUpsertRequest,
    file: File,
  ): Observable<EmploymentContract> {
    return this.api
      .updateContractWithDocument(
        contractId,
        mapEmploymentContractRequest(request),
        file,
      )
      .pipe(map(mapEmploymentContract));
  }

  activate(contractId: number): Observable<EmploymentContract> {
    return this.api
      .activateContract(contractId)
      .pipe(map(mapEmploymentContract));
  }

  terminate(
    contractId: number,
    request: EmploymentContractTerminationRequest,
  ): Observable<EmploymentContract> {
    return this.api
      .terminateContract(contractId, request)
      .pipe(map(mapEmploymentContract));
  }

  cancel(contractId: number): Observable<EmploymentContract> {
    return this.api.cancelContract(contractId).pipe(map(mapEmploymentContract));
  }

  listContractTypes(): Observable<ContractCatalogItem[]> {
    return this.mapCatalog(this.api.listContractTypes());
  }

  listContinuityTypes(): Observable<ContractCatalogItem[]> {
    return this.mapCatalog(this.api.listContinuityTypes());
  }

  listRenewalLimits(): Observable<ContractCatalogItem[]> {
    return this.mapCatalog(this.api.listRenewalLimits());
  }

  listWorkingModes(): Observable<ContractCatalogItem[]> {
    return this.mapCatalog(this.api.listWorkingModes());
  }

  listDocuments(employeeId?: number): Observable<EmployeeDocument[]> {
    return this.api
      .listEmployeeDocuments(employeeId)
      .pipe(map((items) => items.map(mapEmployeeDocument)));
  }

  registerDocument(
    request: EmployeeDocumentRegisterRequest,
  ): Observable<EmployeeDocument> {
    return this.api
      .registerEmployeeDocument(mapEmployeeDocumentRequest(request))
      .pipe(map(mapEmployeeDocument));
  }

  archiveDocument(documentId: number): Observable<EmployeeDocument> {
    return this.api
      .archiveEmployeeDocument(documentId)
      .pipe(map(mapEmployeeDocument));
  }

  private mapCatalog(
    source: ReturnType<StaffEmploymentService["listContractTypes"]>,
  ): Observable<ContractCatalogItem[]> {
    return source.pipe(map((items) => items.map(mapContractCatalogItem)));
  }
}
